import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { getTrackingSettings } from '../../utils/api';
import {
    isPrivateApplicationPath,
    useCookiePreferences,
} from '../privacy/cookieConsent';

const SCRIPT_ID = 'ekc-lucky-orange-tracker';
const SCRIPT_BASE_URL = 'https://tools.luckyorange.com/core/lo.js?site-id=';
const expireLuckyOrangeCookies = () => {
    document.cookie
        .split(';')
        .map((cookie) => cookie.trim().split('=')[0])
        .filter((name) => /^(lo_|_lo|luckyorange)/i.test(name))
        .forEach((name) => {
            document.cookie = `${name}=; Max-Age=0; path=/; SameSite=Lax`;
        });
};

export const disableLuckyOrange = () => {
    document.querySelectorAll<HTMLScriptElement>('script[src*="luckyorange.com"]')
        .forEach((script) => script.remove());
    document.getElementById(SCRIPT_ID)?.remove();
    delete (window as any).LO;
    expireLuckyOrangeCookies();
};

const LuckyOrangeTracking = () => {
    const { pathname } = useLocation();
    const preferences = useCookiePreferences();
    const shouldMonitorPath = preferences?.analytics === true && !isPrivateApplicationPath(pathname);

    useEffect(() => {
        let active = true;

        if (!shouldMonitorPath) {
            disableLuckyOrange();
            return () => { active = false; };
        }

        getTrackingSettings()
            .then((response) => {
                if (!active) return;

                const settings = response?.data?.luckyOrange;
                const siteId = String(settings?.siteId || '').trim();
                const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;

                if (settings?.enabled !== true || !siteId) {
                    disableLuckyOrange();
                    return;
                }

                if (existing?.dataset.siteId === siteId) return;
                existing?.remove();

                const script = document.createElement('script');
                script.id = SCRIPT_ID;
                script.dataset.siteId = siteId;
                script.async = true;
                script.defer = true;
                script.src = `${SCRIPT_BASE_URL}${encodeURIComponent(siteId)}`;
                document.head.appendChild(script);
            })
            .catch(() => {
                // Monitoring fails closed when CMS settings cannot be loaded.
                disableLuckyOrange();
            });

        return () => { active = false; };
    }, [shouldMonitorPath]);

    return null;
};

export default LuckyOrangeTracking;
